// The transport of the season timelapse: PLAY or PAUSE, the stamp of the
// frame, the scrubber and the speed (ui-map-interface.md §3.6, R1620).
//
// The row is the same in the three places. Each of them stands inside a
// host that scrolls under the same finger (the summary body, the WAR tab
// body, the reader pane), so a button acts on the RELEASE through the
// `ListTap` recogniser and never on the first pixel of a scroll (§16.8,
// R1206). The scrubber is a native range: a drag is a stream of `input`
// events, and the press that starts it carries no value.
//
// The binding owns no markup of the host. It finds its elements by class
// at each update, so the host may draw the row inside a larger markup.

import { timelapse as T } from '../../gen/strings';
import { esc } from '../esc';
import { ListTap } from '../touch';
import type { TimelapsePlayer } from './player';

/** The glyphs of the play button. A screen reader reads its label. */
const GLYPH_PLAY = '▶';
const GLYPH_PAUSE = '❚❚';

/** The transport row as markup, at the player's state. `post` is markup
 *  that stands after the speed button. */
export function transportHtml(p: TimelapsePlayer, stamp: string, post = ''): string {
  const play = p.playing ? T.pause : T.play;
  return '<div class="tlx">'
    + `<button type="button" class="tlp" data-tl="play" aria-label="${esc(play)}" title="${esc(play)}">`
    + `${p.playing ? GLYPH_PAUSE : GLYPH_PLAY}</button>`
    + `<span class="tlday">${esc(stamp)}</span>`
    + `<input class="tlr" type="range" min="0" max="${p.max}" step="1" value="${p.frame}"`
    + ` aria-label="${esc(T.scrub)}">`
    + `<button type="button" class="tlsp" data-tl="speed" title="${esc(T.speed_title)}">`
    + `${esc(T.speed({ n: p.speed }))}</button>${post}</div>`;
}

/** What a binding needs beyond the player. */
export interface TransportOpts {
  /** The stamp of one position. */
  stamp: (frame: number) => string;
  /** A press on a `data-tl` button that is not PLAY or the speed. */
  onAct?: (act: string, btn: HTMLElement) => void;
  /** True when the last press became a title hold (R1555): the lift of
   *  that press shows the title and acts on nothing. */
  held?: () => boolean;
}

/** Wires one host's transport to one player. */
export class TransportBinding {
  private readonly tap = new ListTap<HTMLElement>();
  private readonly off: () => void;

  constructor(
    private readonly host: HTMLElement,
    private readonly player: TimelapsePlayer,
    private readonly opts: TransportOpts,
  ) {
    this.tap.onTap = (target) => {
      if (this.opts.held?.()) return;
      const btn = target.closest('[data-tl]');
      if (btn instanceof HTMLElement && host.contains(btn)) this.act(btn);
    };
    host.addEventListener('pointerdown', this.onDown);
    host.addEventListener('pointermove', this.onMove);
    host.addEventListener('pointerup', this.onUp);
    host.addEventListener('pointercancel', this.onCancel);
    host.addEventListener('click', this.onKeyClick);
    host.addEventListener('input', this.onInput);
    this.off = player.on(() => this.update());
  }

  /** Writes the player's state into the row: the stamp, the scrubber, the
   *  glyph of PLAY and the speed. A value that stands is not written, so a
   *  drag is never fought. */
  update(): void {
    const p = this.player;
    const stamp = this.opts.stamp(p.frame);
    for (const el of this.host.querySelectorAll<HTMLElement>('.tlday')) {
      if (el.textContent !== stamp) el.textContent = stamp;
    }
    for (const el of this.host.querySelectorAll<HTMLInputElement>('input.tlr')) {
      if (el.max !== String(p.max)) el.max = String(p.max);
      if (el.value !== String(p.frame)) el.value = String(p.frame);
    }
    const label = p.playing ? T.pause : T.play;
    const glyph = p.playing ? GLYPH_PAUSE : GLYPH_PLAY;
    for (const el of this.host.querySelectorAll<HTMLElement>('.tlp')) {
      if (el.textContent !== glyph) el.textContent = glyph;
      el.setAttribute('aria-label', label);
      el.title = label;
    }
    const speed = T.speed({ n: p.speed });
    for (const el of this.host.querySelectorAll<HTMLElement>('.tlsp')) {
      if (el.textContent !== speed) el.textContent = speed;
    }
  }

  /** Takes every listener off the host and off the player. */
  dispose(): void {
    this.off();
    this.host.removeEventListener('pointerdown', this.onDown);
    this.host.removeEventListener('pointermove', this.onMove);
    this.host.removeEventListener('pointerup', this.onUp);
    this.host.removeEventListener('pointercancel', this.onCancel);
    this.host.removeEventListener('click', this.onKeyClick);
    this.host.removeEventListener('input', this.onInput);
  }

  private act(btn: HTMLElement): void {
    const act = btn.dataset.tl ?? '';
    if (act === 'play') this.player.toggle();
    else if (act === 'speed') this.player.cycleSpeed();
    else this.opts.onAct?.(act, btn);
  }

  private readonly onDown = (ev: PointerEvent): void => {
    if (ev.button > 0) return;
    this.tap.down(ev.pointerId, ev.clientX, ev.clientY, ev.target as HTMLElement);
  };
  private readonly onMove = (ev: PointerEvent): void => { this.tap.move(ev.pointerId, ev.clientX, ev.clientY); };
  private readonly onUp = (ev: PointerEvent): void => { this.tap.up(ev.pointerId, ev.clientX, ev.clientY); };
  private readonly onCancel = (): void => { this.tap.cancel(); };

  /** Enter or Space on a focused button: a native button turns either into
   *  a click with `detail` 0, so a pointer press never acts twice. */
  private readonly onKeyClick = (ev: MouseEvent): void => {
    if (ev.detail !== 0) return;
    const btn = (ev.target as Element | null)?.closest?.('[data-tl]');
    if (btn instanceof HTMLElement && this.host.contains(btn)) this.act(btn);
  };

  private readonly onInput = (ev: Event): void => {
    const el = ev.target as HTMLElement | null;
    if (!(el instanceof HTMLInputElement) || !el.classList.contains('tlr')) return;
    this.player.seek(Number(el.value));
  };
}
